import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Rocket, FileText, CheckCircle, Clock, GraduationCap, 
  Users, TrendingUp, ArrowRight, DollarSign, Calendar, Star, 
  Award, Sparkles, BookOpen, Target, Zap, Bell
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cohort 1 starts 1 July 2026 09:00 GST (UAE = UTC+4 = 05:00 UTC)
const COHORT_TARGET = new Date('2026-07-01T05:00:00Z').getTime();
// Invest UAE Forum 31 May 2026 18:00 BST (17:00 UTC)
const FORUM_TARGET = new Date('2026-05-31T17:00:00Z').getTime();

const computeCD = (target) => {
  const diff = Math.max(0, target - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
  };
};

const FounderDashboard = () => {
  const navigate = useNavigate();
  const { user, isPaid, paymentTier } = useAuth();
  const [stats, setStats] = useState({ applications: 0, mentors: 6, startups: 45 });
  const [myApplications, setMyApplications] = useState([]);
  const [pitchBookings, setPitchBookings] = useState([]);
  const [cohortCD, setCohortCD] = useState(computeCD(COHORT_TARGET));
  const [forumCD, setForumCD] = useState(computeCD(FORUM_TARGET));

  // Calculate journey progress
  const journeySteps = [
    { id: 'account', label: 'Account Created', completed: true },
    { id: 'application', label: 'Submit Application', completed: myApplications.length > 0 },
    { id: 'evaluation', label: 'Nova AI Evaluation', completed: myApplications.some(a => a.status === 'evaluated') },
    { id: 'payment', label: 'Payment & Enrollment', completed: isPaid },
    { id: 'masterclass', label: 'Start Masterclass', completed: false }
  ];
  const completedSteps = journeySteps.filter(s => s.completed).length;
  const progress = (completedSteps / journeySteps.length) * 100;

  // Determine the founder's "Next Best Action"
  const nextAction = (() => {
    if (myApplications.length === 0) {
      return { icon: FileText, label: 'Submit your Cohort 1 application', cta: 'Apply Now', route: '/apply', urgency: 'high' };
    }
    if (!myApplications.some(a => a.status === 'evaluated')) {
      return { icon: Sparkles, label: 'Run Nova AI evaluation on your application', cta: 'Run Evaluation', route: '/chat', urgency: 'medium' };
    }
    if (!isPaid) {
      return { icon: DollarSign, label: 'Secure your seat — Premium AED 1,099/yr', cta: 'Upgrade', route: '/pricing', urgency: 'high' };
    }
    return { icon: BookOpen, label: 'Start the AI Masterclass — Week 1', cta: 'Begin Lesson', route: '/masterclass', urgency: 'low' };
  })();

  useEffect(() => {
    fetchData();
    const t = setInterval(() => {
      setCohortCD(computeCD(COHORT_TARGET));
      setForumCD(computeCD(FORUM_TARGET));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, appsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/my-applications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/pitch-bookings/me`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setMyApplications(appsRes.data || []);
      setPitchBookings(bookingsRes.data || []);
    } catch (e) {
      console.error('Error fetching data:', e);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <Header />
      
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#C8A84B] to-[#D9BC6A] text-[#07070A] py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Rocket className="h-8 w-8" />
                <h1 className="text-3xl font-bold text-[#0D1B2A]">Welcome, {user?.full_name || 'Founder'}!</h1>
                {isPaid && (
                  <Badge className="bg-[#C8A84B] text-[#07070A] font-bold">
                    <Award className="h-3 w-3 mr-1" /> ENROLLED
                  </Badge>
                )}
              </div>
              <p className="opacity-90">Your SKI AXIS Founder Dashboard</p>
            </div>
            {!isPaid && (
              <Button 
                onClick={() => navigate('/pricing')} 
                className="bg-[#FAF7F0] text-[#C8A84B] hover:bg-white border border-[#C8A84B] font-bold shadow-lg"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Join for $9 - Cohort 1
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">

        {/* Live Countdowns + Next Best Action */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Invest UAE Forum countdown */}
          <Card
            data-testid="forum-countdown-card"
            onClick={() => navigate('/events/invest-uae-forum-2026')}
            className="cursor-pointer bg-gradient-to-br from-[#0D1B2A] to-[#1a2744] text-white border-0 hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[#C8A84B] animate-pulse" />
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#C8A84B]">Flagship · Free</span>
              </div>
              <div className="text-lg font-semibold leading-tight mb-3">Invest UAE Forum 2026</div>
              <div className="grid grid-cols-4 gap-1.5 mb-2">
                {[['Days', forumCD.days], ['Hrs', forumCD.hours], ['Min', forumCD.mins], ['Sec', forumCD.secs]].map(([l, v]) => (
                  <div key={l} className="bg-white/8 border border-[#C8A84B]/30 rounded px-1 py-2 text-center">
                    <div className="font-serif text-2xl font-bold text-[#C8A84B] leading-none tabular-nums">{String(v).padStart(2, '0')}</div>
                    <div className="text-[8px] tracking-[0.18em] uppercase text-white/60 mt-1">{l}</div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-white/70 mt-2">31 May 2026 · 6–8 PM BST · Online</div>
            </CardContent>
          </Card>

          {/* Cohort 1 countdown */}
          <Card
            data-testid="cohort-countdown-card"
            onClick={() => navigate('/pricing')}
            className="cursor-pointer bg-gradient-to-br from-[#C8A84B] to-[#B5944A] text-[#0D1B2A] border-0 hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Rocket className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase">Cohort 1 · Starts</span>
              </div>
              <div className="text-lg font-semibold leading-tight mb-3">1 July 2026</div>
              <div className="grid grid-cols-4 gap-1.5 mb-2">
                {[['Days', cohortCD.days], ['Hrs', cohortCD.hours], ['Min', cohortCD.mins], ['Sec', cohortCD.secs]].map(([l, v]) => (
                  <div key={l} className="bg-[#0D1B2A]/10 border border-[#0D1B2A]/20 rounded px-1 py-2 text-center">
                    <div className="font-serif text-2xl font-bold leading-none tabular-nums">{String(v).padStart(2, '0')}</div>
                    <div className="text-[8px] tracking-[0.18em] uppercase opacity-70 mt-1">{l}</div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] opacity-80 mt-2">20 chosen founders · 6 weeks · ends 12 Aug</div>
            </CardContent>
          </Card>

          {/* Next Best Action */}
          <Card
            data-testid="next-action-card"
            onClick={() => navigate(nextAction.route)}
            className="cursor-pointer bg-white border-2 border-dashed border-[#C8A84B] hover:border-solid hover:bg-[#C8A84B]/5 transition-all hover:-translate-y-0.5"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Zap className={`h-3.5 w-3.5 ${nextAction.urgency === 'high' ? 'text-red-500' : nextAction.urgency === 'medium' ? 'text-[#C8A84B]' : 'text-emerald-600'}`} />
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#C8A84B]">Next Best Action</span>
              </div>
              <div className="text-lg font-semibold leading-snug mb-3 text-[#0D1B2A]">{nextAction.label}</div>
              <div className="flex items-center justify-between">
                <Badge className={nextAction.urgency === 'high' ? 'bg-red-100 text-red-700 hover:bg-red-100' : nextAction.urgency === 'medium' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'}>
                  <Bell className="h-3 w-3 mr-1" />
                  {nextAction.urgency === 'high' ? 'High priority' : nextAction.urgency === 'medium' ? 'Up next' : 'Keep going'}
                </Badge>
                <Button size="sm" className="bg-[#C8A84B] hover:bg-[#B5944A] text-[#0D1B2A] font-bold">
                  {nextAction.cta} <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Journey Progress */}
        <Card className="mb-8 border-[rgba(200,168,75,0.2)] bg-gradient-to-r from-[#C8A84B]/8 to-[#C8A84B]/4">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-[#C8A84B]" />
                  Your Journey Progress
                </h3>
                <p className="text-sm text-[#7A8190]">{completedSteps} of {journeySteps.length} steps completed</p>
              </div>
              <span className="text-2xl font-bold text-[#C8A84B]">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-3 mb-4" />
            <div className="flex flex-wrap gap-2">
              {journeySteps.map((step) => (
                <Badge 
                  key={step.id}
                  variant={step.completed ? "default" : "outline"}
                  className={step.completed ? "bg-green-600" : "border-gray-300 text-[#7A8190]"}
                >
                  {step.completed ? <CheckCircle className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                  {step.label}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Enrolled Member Benefits */}
        {isPaid && (
          <Card className="mb-8 border-[rgba(200,168,75,0.3)] bg-gradient-to-r from-[#C8A84B]/12 to-[#C8A84B]/6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#C8A84B]">
                <Award className="h-6 w-6" />
                Cohort 1 Member Benefits
              </CardTitle>
              <CardDescription>Your {paymentTier || 'Launch'} tier access</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-white border border-[rgba(200,168,75,0.3)] rounded-lg">
                  <BookOpen className="h-8 w-8 text-[#C8A84B] mx-auto mb-2" />
                  <p className="font-medium text-sm">6-Week Masterclass</p>
                  <p className="text-xs text-[#7A8190]">Starts July 1, 2026</p>
                </div>
                <div className="text-center p-4 bg-white border border-[rgba(200,168,75,0.3)] rounded-lg">
                  <Users className="h-8 w-8 text-[#C8A84B] mx-auto mb-2" />
                  <p className="font-medium text-sm">Mentor Access</p>
                  <p className="text-xs text-[#7A8190]">6 Expert Mentors</p>
                </div>
                <div className="text-center p-4 bg-white border border-[rgba(200,168,75,0.3)] rounded-lg">
                  <Sparkles className="h-8 w-8 text-[#C8A84B] mx-auto mb-2" />
                  <p className="font-medium text-sm">Nova AI</p>
                  <p className="text-xs text-[#7A8190]">Unlimited Chat</p>
                </div>
                <div className="text-center p-4 bg-white border border-[rgba(200,168,75,0.3)] rounded-lg">
                  <TrendingUp className="h-8 w-8 text-[#C8A84B] mx-auto mb-2" />
                  <p className="font-medium text-sm">Investor Access</p>
                  <p className="text-xs text-[#7A8190]">Pitch Day Ready</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white border border-[rgba(200,168,75,0.3)] hover:border-[rgba(200,168,75,0.4)] transition-all cursor-pointer text-[#0D1B2A]" onClick={() => navigate('/apply')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#C8A84B]/12 rounded-xl">
                  <FileText className="h-8 w-8 text-[#C8A84B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#0D1B2A]">Apply Now</h3>
                  <p className="text-sm text-[#7A8190]">
                    {myApplications.length > 0 ? `${myApplications.length} submitted` : 'Submit your application'}
                  </p>
                </div>
              </div>
              <Button className="w-full mt-4 bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold">
                {myApplications.length > 0 ? 'View Applications' : 'Start Application'} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white border border-[rgba(200,168,75,0.3)] hover:border-[rgba(200,168,75,0.4)] transition-all cursor-pointer text-[#0D1B2A]" onClick={() => navigate('/mentors')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#C8A84B]/10 rounded-xl">
                  <GraduationCap className="h-8 w-8 text-[#C8A84B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#0D1B2A]">Meet Mentors</h3>
                  <p className="text-sm text-[#7A8190]">{stats.mentors || 6} expert mentors</p>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4 border-orange-300 text-orange-700">
                View Mentors <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white border border-[rgba(200,168,75,0.3)] hover:border-[rgba(200,168,75,0.4)] transition-all cursor-pointer text-[#0D1B2A]" onClick={() => navigate('/pitch-day')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#C8A84B]/10 rounded-xl">
                  <TrendingUp className="h-8 w-8 text-[#C8A84B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#0D1B2A]">Pitch Day</h3>
                  <p className="text-sm text-[#7A8190]">
                    {pitchBookings.length > 0 ? `${pitchBookings.length} booked` : 'Book investor calls'}
                  </p>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4 border-blue-300 text-blue-700">
                Book Slot <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Program Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-white border border-[rgba(13,27,42,0.08)] text-[#0D1B2A]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#C8A84B]" />
                Cohort 1 — 1 July 2026
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-[#C8A84B]/8 rounded-lg">
                  <span className="font-medium">Week 1-2</span>
                  <span className="text-[#3A4A5C]">Governance Foundations</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#C8A84B]/8 rounded-lg">
                  <span className="font-medium">Week 3-4</span>
                  <span className="text-[#3A4A5C]">Compliance & Risk</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#C8A84B]/8 rounded-lg">
                  <span className="font-medium">Week 5-6</span>
                  <span className="text-[#3A4A5C]">Board Structure & Controls</span>
                </div>
              </div>
              {!isPaid && (
                <Button onClick={() => navigate('/pricing')} className="w-full mt-4 bg-[#C8A84B] text-[#07070A] font-bold">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Join for $9 (Launch Special)
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border border-[rgba(13,27,42,0.08)] text-[#0D1B2A]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" />
                Your Journey
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {journeySteps.map((step) => (
                  <div 
                    key={step.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg ${step.completed ? 'border-[rgba(200,168,75,0.2)] bg-[#C8A84B]/8' : 'border-dashed border-gray-200'}`}
                  >
                    {step.completed ? (
                      <CheckCircle className="h-5 w-5 text-[#C8A84B]" />
                    ) : (
                      <Clock className="h-5 w-5 text-[#3A4A5C]" />
                    )}
                    <span className={step.completed ? 'text-green-700 font-medium' : 'text-[#7A8190]'}>{step.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Resources */}
        <Card className="bg-white border border-[rgba(13,27,42,0.08)] text-[#0D1B2A]">
          <CardHeader>
            <CardTitle>Resources</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Button variant="outline" onClick={() => navigate('/governance')} className="h-auto p-4 flex flex-col items-center gap-2">
                <Users className="h-6 w-6 text-[#C8A84B]" />
                <span>View Portfolio</span>
                <span className="text-xs text-[#7A8190]">{stats.total_startups || 45} startups</span>
              </Button>
              <Button variant="outline" onClick={() => navigate('/jobs')} className="h-auto p-4 flex flex-col items-center gap-2">
                <TrendingUp className="h-6 w-6 text-[#C8A84B]" />
                <span>Job Board</span>
                <span className="text-xs text-[#7A8190]">Find opportunities</span>
              </Button>
              <Button variant="outline" onClick={() => navigate('/apply-form')} className="h-auto p-4 flex flex-col items-center gap-2">
                <FileText className="h-6 w-6 text-[#C8A84B]" />
                <span>Download Form</span>
                <span className="text-xs text-[#7A8190]">Word template</span>
              </Button>
              <Button variant="outline" onClick={() => navigate('/chat')} className="h-auto p-4 flex flex-col items-center gap-2">
                <Sparkles className="h-6 w-6 text-[#C8A84B]" />
                <span>Chat with Nova</span>
                <span className="text-xs text-[#7A8190]">AI Assistant</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FounderDashboard;
